import { useState, useMemo, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    Button,
    Input,
    Textarea,
    Autocomplete,
    AutocompleteItem,
    Divider,
    Modal,
    ModalContent,
    ModalBody,
    ModalFooter,
    useDisclosure,
    Image,
    Chip,
    DatePicker,
    NumberInput,
    Drawer,
    DrawerContent,
    DrawerHeader,
    DrawerBody,
    DrawerFooter,
    Skeleton,
    Spinner,
} from "@heroui/react";
import { today, getLocalTimeZone, parseDate } from "@internationalized/date";
import {
    LuArrowLeft,
    LuShoppingCart,
    LuTrash2,
    LuPackage,
    LuSearch,
    LuPencil,
} from "react-icons/lu";
import { useForm, Controller } from "react-hook-form";
import { useFetchData, usePostData } from "@/hooks";
import {
    PageHeader,
    ThemedText,
    EmptyState,
    NoImageFallback,
    CreatableAutocomplete,
} from "@/components/ui";
import {
    formatCurrency,
    formatThousands,
    findOption,
    showToast,
} from "@/utils";

const PurchaseOrderCreate = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = !!id;

    // -- Form State --
    const {
        control,
        handleSubmit,
        watch,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: {
            order_date: today(getLocalTimeZone()),
            supplier_id: "",
            payment_method_id: "",
            master_cash_id: "",
            due_date: null,
            giro: {
                pickup_date: null,
                bank_id: "",
            },
            description: "",
        },
    });

    const paymentMethodId = watch("payment_method_id");
    const isCashOrTransfer = ["1", "2", "3"].includes(String(paymentMethodId));

    // -- Data Fetching --
    const fetchSuppliers = useFetchData("select/supplier");
    const fetchPaymentMethods = useFetchData("select/payment-method");
    const fetchBanks = useFetchData("select/bank");
    const fetchMasterCash = useFetchData("select/master-cash");
    const fetchMaterials = useFetchData("select/sales-order-material");
    const fetchWarehouses = useFetchData("select/warehouse");

    // Fetch initial data if editing
    const fetchUrl = isEdit ? `purchase-orders/${id}/edit` : null;
    const {
        data: initialData,
        isLoading: isFetching,
        error: fetchError,
        refetch,
    } = useFetchData(fetchUrl);

    const endpoint = isEdit
        ? `purchase-orders/${id}/update`
        : "purchase-orders/store";
    const storePurchaseOrder = usePostData(endpoint);

    // Populate form and cart when editing
    useEffect(() => {
        if (isEdit && initialData) {
            // Guard: block edit if order status is Done (4) or Cancelled (5)
            if ([4, 5].includes(initialData.order_status_id)) {
                showToast(
                    "error",
                    "Tidak Bisa Diedit",
                    `Purchase Order dengan status ${initialData.order_status?.name || "selesai/dibatalkan"} tidak dapat diubah.`,
                );
                navigate("/purchase-order");
                return;
            }

            // 1. Reset Form
            reset({
                order_date: initialData.order_date
                    ? parseDate(initialData.order_date)
                    : today(getLocalTimeZone()),
                supplier_id: String(initialData.supplier_id),
                payment_method_id: String(initialData.payment_method_id),
                due_date: initialData.due_date
                    ? parseDate(initialData.due_date)
                    : null,
                description: initialData.description || "",
            });

            // 2. Populate Cart
            if (initialData.items && initialData.items.length > 0) {
                const mappedItems = initialData.items.map((item, idx) => ({
                    cart_id: Date.now() + idx + Math.random(),
                    id: item.material_id,
                    name: item.material?.name || "Unknown Item",
                    quantity: parseFloat(item.quantity),
                    unit_id: item.material_unit_id,
                    unit: item.unit?.name || "",
                    price:
                        parseFloat(item.unit_price) ||
                        parseFloat(item.price) ||
                        0,
                    description: item.description,
                    warehouse_id: item.warehouse_id,
                    warehouse: item.warehouse?.name || "",
                    image_url: item.material?.image_url,
                }));
                setCart(mappedItems);
            }
        }
    }, [isEdit, initialData, reset]);

    // -- Cart State --
    const [cart, setCart] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");

    // -- Warehouse Stock State (for display info) --
    const [warehouseStockUrl, setWarehouseStockUrl] = useState(null);
    const fetchWarehouseStock = useFetchData(warehouseStockUrl);

    // -- Modal & Drawer State --
    const itemModal = useDisclosure();
    const cartDrawer = useDisclosure();
    const [selectedItem, setSelectedItem] = useState(null);

    // -- Modal Item Form (separate from main form) --
    const {
        control: itemControl,
        handleSubmit: handleItemSubmit,
        watch: watchItem,
        setValue: setItemValue,
        reset: resetItemForm,
        formState: { errors: itemErrors },
    } = useForm({
        defaultValues: {
            quantity: 1,
            unit_id: null,
            unit: "",
            price: 0,
            warehouse_id: null,
            warehouse: "",
        },
    });

    const itemFormValues = watchItem();

    // -- Derived Data --
    const filteredMaterials = useMemo(() => {
        if (!fetchMaterials.data) return [];
        return fetchMaterials.data.filter((item) =>
            item.name.toLowerCase().includes(searchQuery.toLowerCase()),
        );
    }, [fetchMaterials.data, searchQuery]);

    const totalAmount = useMemo(() => {
        return cart.reduce((sum, item) => sum + item.quantity * item.price, 0);
    }, [cart]);

    // -- Handlers --
    const handleItemClick = (item) => {
        setSelectedItem(item);
        // Find default unit or first available
        const defaultUnit =
            item.units?.find((u) => u.is_default) || item.units?.[0];

        // Fetch warehouse stock for this material + unit (for info display)
        if (item.id && defaultUnit?.unit_id) {
            setWarehouseStockUrl(
                `select/warehouse-stock?material_id=${item.id}&unit_id=${defaultUnit.unit_id}`,
            );
        } else {
            setWarehouseStockUrl(null);
        }

        // Reset modal form with new values
        resetItemForm({
            quantity: 1,
            unit_id: defaultUnit?.unit_id || null,
            unit: defaultUnit?.unit || item.unit || "",
            price: defaultUnit?.price || item.price || 0,
            warehouse_id: null,
            warehouse: "",
        });
        itemModal.onOpen();
    };

    const handleUnitChange = (unitId) => {
        if (!selectedItem?.units) return;
        const unit = selectedItem.units.find(
            (u) => String(u.unit_id) === String(unitId),
        );
        if (unit) {
            // Refetch warehouse stock for new unit
            setWarehouseStockUrl(
                `select/warehouse-stock?material_id=${selectedItem.id}&unit_id=${unit.unit_id}`,
            );

            setItemValue("unit_id", unit.unit_id);
            setItemValue("unit", unit.unit);
            setItemValue("price", unit.price || 0);
        }
    };

    const handleWarehouseChange = (warehouseId) => {
        if (!warehouseId) {
            setItemValue("warehouse_id", null);
            setItemValue("warehouse", "");
            return;
        }

        const warehouse = findOption(fetchWarehouses.data, warehouseId);
        setItemValue("warehouse_id", warehouseId);
        setItemValue("warehouse", warehouse?.title || "");
    };

    // Get current stock in selected warehouse (for info display)
    const getCurrentStock = (warehouseId) => {
        if (!fetchWarehouseStock.data || !warehouseId) return 0;
        const wh = fetchWarehouseStock.data.find(
            (w) => String(w.value) === String(warehouseId),
        );
        return wh?.stock || 0;
    };

    // Form submit handler for modal (receives validated data)
    const onItemSubmit = (data) => {
        if (!selectedItem) return;

        setCart((prev) => {
            // Case 1: Edit existing item in cart (has cart_id)
            if (selectedItem.cart_id) {
                return prev.map((item) =>
                    item.cart_id === selectedItem.cart_id
                        ? {
                              ...selectedItem,
                              quantity: parseFloat(data.quantity),
                              unit_id: data.unit_id,
                              unit: data.unit,
                              price: parseFloat(data.price),
                              warehouse_id: data.warehouse_id,
                              warehouse: data.warehouse,
                          }
                        : item,
                );
            }

            // Case 2: Add new item, check for duplicates (same id, unit, warehouse, price)
            const duplicateIndex = prev.findIndex(
                (item) =>
                    item.id === selectedItem.id &&
                    String(item.unit_id) === String(data.unit_id) &&
                    String(item.warehouse_id) === String(data.warehouse_id) &&
                    parseFloat(item.price) === parseFloat(data.price),
            );

            if (duplicateIndex >= 0) {
                // Accumulate quantity
                const newCart = [...prev];
                const newQuantity =
                    newCart[duplicateIndex].quantity +
                    parseFloat(data.quantity);

                newCart[duplicateIndex] = {
                    ...newCart[duplicateIndex],
                    quantity: newQuantity,
                    price: parseFloat(data.price),
                };
                return newCart;
            }

            // Case 3: Add new unique item
            const newItem = {
                ...selectedItem,
                cart_id: Date.now() + Math.random(),
                quantity: parseFloat(data.quantity),
                unit_id: data.unit_id,
                unit: data.unit,
                price: parseFloat(data.price),
                warehouse_id: data.warehouse_id,
                warehouse: data.warehouse,
            };
            return [...prev, newItem];
        });

        itemModal.onClose();
        setSelectedItem(null);
    };

    const handleRemoveFromCart = (cartId) => {
        setCart((prev) => prev.filter((item) => item.cart_id !== cartId));
    };

    const handleEditCartItem = (cartItem) => {
        setSelectedItem(cartItem);

        // Fetch warehouse stock for this material + unit
        if (cartItem.id && cartItem.unit_id) {
            setWarehouseStockUrl(
                `select/warehouse-stock?material_id=${cartItem.id}&unit_id=${cartItem.unit_id}`,
            );
        }

        // Reset form with cart item values
        resetItemForm({
            quantity: cartItem.quantity,
            unit_id: cartItem.unit_id,
            unit: cartItem.unit,
            price: cartItem.price,
            warehouse_id: cartItem.warehouse_id || null,
            warehouse: cartItem.warehouse || "",
        });
        itemModal.onOpen();
    };

    const onSubmit = (data) => {
        if (cart.length === 0) {
            showToast("error", "Error", "Keranjang belanja masih kosong!");
            return;
        }

        const payload = {
            ...data,
            order_date: data.order_date.toString(),
            due_date: data.due_date ? data.due_date.toString() : null,
            order_status_id: 1, // Created
            payment_status_id: 1, // Unpaid
            total_amount: totalAmount,
            items: cart.map((item) => ({
                material_id: item.id,
                warehouse_id: item.warehouse_id,
                material_unit_id: item.unit_id,
                quantity: item.quantity,
                price: item.price,
                description: item.description,
            })),
        };

        if (isEdit) {
            payload._method = "PUT";
        }

        storePurchaseOrder.mutate(payload, {
            onSuccess: () => {
                showToast(
                    "success",
                    "Berhasil",
                    `Pembelian berhasil ${isEdit ? "diperbarui" : "dibuat"}!`,
                );
                navigate("/purchase-order");
            },
            onError: (err) => {
                showToast(
                    "error",
                    "Gagal",
                    err.response?.data?.message ||
                        `Gagal ${isEdit ? "memperbarui" : "membuat"} Pembelian`,
                );
            },
        });
    };

    if (isEdit && isFetching) {
        return (
            <div className="flex h-[calc(100vh-3rem)] w-full items-center justify-center">
                <Spinner
                    label="Memuat data purchase order..."
                    color="primary"
                />
            </div>
        );
    }

    if (isEdit && fetchError) {
        return (
            <EmptyState
                title="Gagal Memuat Data"
                description="Terjadi kesalahan saat memuat data. Silakan coba lagi atau hubungi administrator."
            />
        );
    }

    return (
        <div className="flex w-full flex-col gap-4 md:h-[calc(100vh-3rem)] md:flex-row md:overflow-hidden">
            {/* Left Side: Form & Items */}
            <div className="flex w-full flex-col gap-4 md:flex-1 md:overflow-hidden">
                {/* Header */}
                <div className="flex gap-4">
                    <Button
                        isIconOnly
                        variant="light"
                        onPress={() => navigate("/purchase-order")}
                    >
                        <LuArrowLeft size={20} />
                    </Button>
                    <PageHeader
                        title={
                            isEdit
                                ? "Edit Pembelian"
                                : "Tambah Pembelian"
                        }
                        breadcrumbs={[
                            { label: "Dashboard", href: "/" },
                            {
                                label: "Pembelian",
                                href: "/purchase-order",
                            },
                            { label: isEdit ? "Edit" : "Tambah" },
                        ]}
                    />
                </div>

                {/* Main Content Card */}
                <Card
                    className="border-default-200 flex flex-col border md:flex-1 md:flex-row md:overflow-hidden"
                    shadow="none"
                >
                    <CardBody className="flex flex-col gap-6 p-4 md:flex-1 md:flex-row md:overflow-hidden">
                        {/* Order Info Section */}
                        <div className="flex w-full flex-col gap-4 md:w-2/5">
                            <ThemedText className="font-semibold">
                                Informasi Order
                            </ThemedText>

                            <div className="flex flex-col gap-4">
                                <Controller
                                    name="order_date"
                                    control={control}
                                    rules={{ required: "Tanggal order wajib diisi" }}
                                    render={({ field }) => (
                                        <DatePicker
                                            label="Tanggal Order"
                                            value={field.value}
                                            onChange={field.onChange}
                                            variant="bordered"
                                            isRequired
                                            showMonthAndYearPickers
                                        />
                                    )}
                                />

                                <Controller
                                    name="supplier_id"
                                    control={control}
                                    rules={{
                                        required: "Supplier wajib dipilih",
                                    }}
                                    render={({ field }) => (
                                        <Autocomplete
                                            label="Supplier"
                                            placeholder="Cari supplier..."
                                            variant="bordered"
                                            isRequired
                                            defaultItems={
                                                fetchSuppliers.data || []
                                            }
                                            selectedKey={field.value}
                                            onSelectionChange={field.onChange}
                                            isLoading={fetchSuppliers.isLoading}
                                            isInvalid={!!errors.supplier_id}
                                            errorMessage={
                                                errors.supplier_id?.message
                                            }
                                        >
                                            {(item) => (
                                                <AutocompleteItem
                                                    key={item.value}
                                                >
                                                    {item.title}
                                                </AutocompleteItem>
                                            )}
                                        </Autocomplete>
                                    )}
                                />

                                <Controller
                                    name="payment_method_id"
                                    control={control}
                                    rules={{
                                        required:
                                            "Metode pembayaran wajib dipilih",
                                    }}
                                    render={({ field }) => (
                                        <CreatableAutocomplete
                                            label="Metode Pembayaran"
                                            placeholder="Pilih metode pembayaran"
                                            variant="bordered"
                                            isRequired
                                            items={
                                                fetchPaymentMethods.data || []
                                            }
                                            selectedKey={field.value}
                                            onSelectionChange={field.onChange}
                                            storeUrl="payment-method/store"
                                            isLoading={
                                                fetchPaymentMethods.isLoading
                                            }
                                            isInvalid={
                                                !!errors.payment_method_id
                                            }
                                            errorMessage={
                                                errors.payment_method_id
                                                    ?.message
                                            }
                                        />
                                    )}
                                />

                                {/* Conditional Master Cash for Cash/Transfer/QRIS */}
                                {isCashOrTransfer && (
                                    <Controller
                                        name="master_cash_id"
                                        control={control}
                                        rules={{
                                            required: "Sumber kas wajib dipilih",
                                        }}
                                        render={({ field }) => (
                                            <Autocomplete
                                                label="Sumber Kas"
                                                placeholder="Pilih sumber kas"
                                                variant="bordered"
                                                isRequired
                                                selectedKey={field.value}
                                                onSelectionChange={field.onChange}
                                                isLoading={fetchMasterCash.isLoading}
                                                isInvalid={!!errors.master_cash_id}
                                                errorMessage={errors.master_cash_id?.message}
                                                description="Pembayaran otomatis terbuat — status otomatis lunas"
                                            >
                                                {(fetchMasterCash.data || []).map((item) => (
                                                    <AutocompleteItem key={item.value}>
                                                        {item.title}
                                                    </AutocompleteItem>
                                                ))}
                                            </Autocomplete>
                                        )}
                                    />
                                )}

                                {/* Jatuh Tempo */}
                                <Controller
                                    name="due_date"
                                    control={control}
                                    render={({ field }) => (
                                        <DatePicker
                                            label="Jatuh Tempo (Opsional)"
                                            value={field.value}
                                            onChange={field.onChange}
                                            variant="bordered"
                                            showMonthAndYearPickers
                                        />
                                    )}
                                />

                                {/* Conditional Giro Fields */}
                                {String(paymentMethodId) === "7" && (
                                    <div className="flex flex-col gap-4">
                                        <div className="flex items-center gap-2">
                                            <Divider className="flex-1" />
                                            <ThemedText size="sm">
                                                Informasi Giro
                                            </ThemedText>
                                            <Divider className="flex-1" />
                                        </div>
                                        <Controller
                                            name="giro.pickup_date"
                                            control={control}
                                            rules={{
                                                required:
                                                    "Tanggal giro wajib diisi",
                                            }}
                                            render={({ field }) => (
                                                <DatePicker
                                                    label="Tanggal Jatuh Tempo Giro"
                                                    variant="bordered"
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    isInvalid={
                                                        !!errors.giro
                                                            ?.pickup_date
                                                    }
                                                    errorMessage={
                                                        errors.giro?.pickup_date
                                                            ?.message
                                                    }
                                                    showMonthAndYearPickers
                                                />
                                            )}
                                        />
                                        <Controller
                                            name="giro.bank_id"
                                            control={control}
                                            rules={{
                                                required: "Bank wajib dipilih",
                                            }}
                                            render={({ field }) => (
                                                <Autocomplete
                                                    label="Bank"
                                                    placeholder="Pilih bank"
                                                    variant="bordered"
                                                    defaultItems={
                                                        fetchBanks.data || []
                                                    }
                                                    selectedKey={field.value}
                                                    onSelectionChange={
                                                        field.onChange
                                                    }
                                                    isLoading={
                                                        fetchBanks.isLoading
                                                    }
                                                    isInvalid={
                                                        !!errors.giro?.bank_id
                                                    }
                                                    errorMessage={
                                                        errors.giro?.bank_id
                                                            ?.message
                                                    }
                                                >
                                                    {(item) => (
                                                        <AutocompleteItem
                                                            key={item.value}
                                                        >
                                                            {item.title}
                                                        </AutocompleteItem>
                                                    )}
                                                </Autocomplete>
                                            )}
                                        />
                                    </div>
                                )}

                                {/* Catatan */}
                                <Controller
                                    name="description"
                                    control={control}
                                    render={({ field }) => (
                                        <Textarea
                                            label="Catatan (Opsional)"
                                            placeholder="Catatan tambahan..."
                                            variant="bordered"
                                            minRows={3}
                                            maxRows={6}
                                            value={field.value}
                                            onValueChange={field.onChange}
                                        />
                                    )}
                                />
                            </div>
                        </div>

                        {/* Product Grid Section */}
                        <div className="flex flex-1 flex-col gap-4 overflow-hidden">
                            <div className="flex items-center justify-between">
                                <ThemedText className="font-semibold">
                                    Pilih Produk
                                </ThemedText>
                                <div className="w-64">
                                    <Input
                                        placeholder="Cari produk..."
                                        startContent={<LuSearch />}
                                        size="sm"
                                        value={searchQuery}
                                        onValueChange={setSearchQuery}
                                    />
                                </div>
                            </div>

                            <div className="grid flex-1 auto-rows-min grid-cols-2 gap-3 overflow-y-auto pr-1 pb-4 sm:grid-cols-3 2xl:grid-cols-4">
                                {fetchMaterials.isLoading ? (
                                    [...Array(8)].map((_, index) => (
                                        <Card
                                            key={`skeleton-${index}`}
                                            className="border-default-200 w-full border"
                                            shadow="none"
                                            radius="sm"
                                        >
                                            <CardBody className="overflow-hidden p-0">
                                                <Skeleton className="aspect-square w-full rounded-none" />
                                                <div className="flex w-full flex-col gap-2 p-2">
                                                    <Skeleton className="h-4 w-3/4 rounded" />
                                                    <Skeleton className="h-3 w-1/2 rounded" />
                                                    <Skeleton className="h-4 w-2/3 rounded" />
                                                </div>
                                            </CardBody>
                                        </Card>
                                    ))
                                ) : filteredMaterials.length > 0 ? (
                                    filteredMaterials.map((item) => (
                                        <Card
                                            key={item.id}
                                            isPressable
                                            onPress={() =>
                                                handleItemClick(item)
                                            }
                                            className="hover:border-primary-500 border-default-200 w-full border transition-all hover:shadow-sm"
                                            shadow="none"
                                            radius="sm"
                                        >
                                            <CardBody className="overflow-hidden p-0">
                                                {item.image_url ? (
                                                    <Image
                                                        width="100%"
                                                        alt={item.name}
                                                        className="aspect-square w-full object-cover"
                                                        src={item.image_url}
                                                        radius="none"
                                                    />
                                                ) : (
                                                    <div className="bg-default-100 flex aspect-square w-full items-center justify-center">
                                                        <NoImageFallback
                                                            iconSize={32}
                                                            icon={LuPackage}
                                                        />
                                                    </div>
                                                )}
                                                <div className="flex w-full flex-col items-start gap-0.5 p-2">
                                                    <ThemedText
                                                        size="sm"
                                                        className="line-clamp-2 p-0 font-semibold"
                                                    >
                                                        {item.name}
                                                    </ThemedText>
                                                    <ThemedText
                                                        size="xs"
                                                        variant="secondary"
                                                        className="line-clamp-1"
                                                    >
                                                        {item.category} -{" "}
                                                        {item.sub_category}
                                                    </ThemedText>
                                                    <div className="flex w-full items-center">
                                                        <ThemedText
                                                            size="sm"
                                                            variant="primary"
                                                            className="font-semibold"
                                                        >
                                                            {formatCurrency(
                                                                item.price,
                                                            )}
                                                        </ThemedText>
                                                        <ThemedText
                                                            size="xs"
                                                            variant="caption"
                                                        >
                                                            /{item.unit}
                                                        </ThemedText>
                                                    </div>
                                                </div>
                                            </CardBody>
                                        </Card>
                                    ))
                                ) : (
                                    <div className="col-span-full py-10">
                                        <EmptyState
                                            title="Tidak ada produk"
                                            description="Belum ada data produk tersedia."
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </CardBody>
                </Card>
            </div>

            {/* Right Side: Cart Summary (Desktop) */}
            <Card
                className="border-default-200 hidden h-auto w-full flex-col border md:flex md:h-full md:w-[350px] md:overflow-hidden"
                shadow="none"
            >
                <CardHeader className="border-default-200 flex items-center justify-between gap-4 border-b p-4">
                    <div className="flex items-center gap-2">
                        <LuShoppingCart size={18} />
                        <ThemedText className="font-semibold">
                            Daftar Pembelian
                        </ThemedText>
                    </div>
                    <Chip size="sm" color="primary" variant="flat">
                        {cart.length} Item
                    </Chip>
                </CardHeader>

                <CardBody className="flex flex-1 flex-col gap-2 overflow-y-auto p-4">
                    {cart.length === 0 ? (
                        <div className="flex h-full flex-col items-center justify-center gap-2">
                            <LuPackage size={40} className="text-default-400" />
                            <ThemedText size="sm" variant="secondary">
                                Belum ada item
                            </ThemedText>
                        </div>
                    ) : (
                        cart.map((item, index) => (
                            <Card
                                key={item.cart_id || index}
                                className="bg-default-50 dark:bg-default-100"
                                shadow="none"
                            >
                                <CardBody className="flex flex-col gap-2 p-3">
                                    <div className="flex justify-between">
                                        <div className="flex flex-col">
                                            <ThemedText
                                                size="sm"
                                                className="font-medium"
                                            >
                                                {item.name}
                                            </ThemedText>
                                            <ThemedText
                                                size="xs"
                                                variant="caption"
                                            >
                                                Simpan di {item.warehouse}
                                            </ThemedText>
                                        </div>
                                        <ThemedText
                                            size="sm"
                                            className="font-semibold"
                                        >
                                            {formatCurrency(
                                                item.price * item.quantity,
                                            )}
                                        </ThemedText>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div className="flex flex-col">
                                            <ThemedText
                                                size="xs"
                                                variant="secondary"
                                            >
                                                {formatThousands(item.quantity)}{" "}
                                                {item.unit} x{" "}
                                                {formatCurrency(item.price)}
                                            </ThemedText>
                                        </div>
                                        <div className="flex gap-1">
                                            <Button
                                                isIconOnly
                                                size="sm"
                                                variant="light"
                                                onPress={() =>
                                                    handleEditCartItem(item)
                                                }
                                                className="h-6 w-6 min-w-0"
                                            >
                                                <LuPencil size={14} />
                                            </Button>
                                            <Button
                                                isIconOnly
                                                size="sm"
                                                variant="light"
                                                color="danger"
                                                onPress={() =>
                                                    handleRemoveFromCart(
                                                        item.cart_id,
                                                    )
                                                }
                                                className="h-6 w-6 min-w-0"
                                            >
                                                <LuTrash2 size={14} />
                                            </Button>
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        ))
                    )}
                </CardBody>

                <CardFooter className="border-default-200 bg-default-50 flex flex-col gap-4 border-t p-4">
                    <div className="flex w-full items-center justify-between">
                        <ThemedText size="sm" variant="secondary">
                            Total Pembelian
                        </ThemedText>
                        <ThemedText
                            size="lg"
                            variant="primary"
                            className="font-bold"
                        >
                            {formatCurrency(totalAmount)}
                        </ThemedText>
                    </div>
                    <Button
                        color="primary"
                        className="w-full font-semibold"
                        onPress={handleSubmit(onSubmit)}
                        isLoading={storePurchaseOrder.isLoading}
                        isDisabled={cart.length === 0}
                    >
                        Simpan Pembelian
                    </Button>
                </CardFooter>
            </Card>

            {/* Mobile Fixed Footer */}
            <div className="bg-content1 border-default-200 fixed right-0 bottom-0 left-0 z-50 flex items-center justify-between border-t p-4 md:hidden">
                <div className="flex flex-col">
                    <ThemedText size="xs" variant="secondary">
                        Total {cart.length} Item
                    </ThemedText>
                    <ThemedText className="font-bold">
                        {formatCurrency(totalAmount)}
                    </ThemedText>
                </div>
                <Button
                    color="primary"
                    className="font-semibold"
                    size="sm"
                    onPress={cartDrawer.onOpen}
                    endContent={<LuShoppingCart />}
                >
                    Lihat Daftar
                </Button>
            </div>

            {/* Mobile Cart Drawer */}
            <Drawer
                isOpen={cartDrawer.isOpen}
                onOpenChange={cartDrawer.onOpenChange}
                placement="bottom"
                className="h-[85vh]"
            >
                <DrawerContent>
                    {(onClose) => (
                        <>
                            <DrawerHeader className="flex flex-col gap-1">
                                <div className="flex items-center gap-2">
                                    <LuShoppingCart size={20} />
                                    <ThemedText className="font-semibold">
                                        Daftar Pembelian
                                    </ThemedText>
                                    <Chip
                                        size="sm"
                                        variant="flat"
                                        color="success"
                                    >
                                        {cart.length} Item
                                    </Chip>
                                </div>
                            </DrawerHeader>
                            <DrawerBody>
                                {cart.length === 0 ? (
                                    <div className="flex h-full flex-col items-center justify-center gap-2">
                                        <LuPackage
                                            size={40}
                                            className="text-default-400"
                                        />
                                        <ThemedText
                                            size="sm"
                                            variant="secondary"
                                        >
                                            Belum ada item
                                        </ThemedText>
                                    </div>
                                ) : (
                                    cart.map((item, index) => (
                                        <Card
                                            key={`${item.id}-${index}`}
                                            className="bg-default-50 dark:bg-default-100 shrink-0"
                                            shadow="none"
                                        >
                                            <CardBody className="flex flex-col gap-2 p-3">
                                                <div className="flex justify-between">
                                                    <ThemedText
                                                        size="sm"
                                                        className="font-medium"
                                                    >
                                                        {item.name}
                                                    </ThemedText>
                                                    <ThemedText
                                                        size="sm"
                                                        className="font-semibold"
                                                    >
                                                        {formatCurrency(
                                                            item.price *
                                                                item.quantity,
                                                        )}
                                                    </ThemedText>
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <ThemedText
                                                        size="xs"
                                                        variant="secondary"
                                                    >
                                                        {formatThousands(
                                                            item.quantity,
                                                        )}{" "}
                                                        {item.unit} x{" "}
                                                        {formatCurrency(
                                                            item.price,
                                                        )}
                                                    </ThemedText>
                                                    <div className="flex gap-1">
                                                        <Button
                                                            isIconOnly
                                                            size="sm"
                                                            variant="light"
                                                            onPress={() => {
                                                                handleEditCartItem(
                                                                    item,
                                                                );
                                                                onClose();
                                                            }}
                                                            className="h-6 w-6 min-w-0"
                                                        >
                                                            <LuPencil
                                                                size={14}
                                                            />
                                                        </Button>
                                                        <Button
                                                            isIconOnly
                                                            size="sm"
                                                            variant="light"
                                                            color="danger"
                                                            onPress={() =>
                                                                handleRemoveFromCart(
                                                                    item.cart_id,
                                                                )
                                                            }
                                                            className="h-6 w-6 min-w-0"
                                                        >
                                                            <LuTrash2
                                                                size={14}
                                                            />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </CardBody>
                                        </Card>
                                    ))
                                )}
                            </DrawerBody>
                            <DrawerFooter className="flex-col gap-4">
                                <div className="flex w-full items-center justify-between">
                                    <ThemedText size="sm" variant="secondary">
                                        Total
                                    </ThemedText>
                                    <ThemedText
                                        variant="primary"
                                        className="font-bold"
                                    >
                                        {formatCurrency(totalAmount)}
                                    </ThemedText>
                                </div>
                                <Button
                                    color="primary"
                                    className="w-full font-semibold"
                                    onPress={handleSubmit(onSubmit)}
                                    isLoading={storePurchaseOrder.isLoading}
                                    isDisabled={cart.length === 0}
                                >
                                    Simpan Pembelian
                                </Button>
                            </DrawerFooter>
                        </>
                    )}
                </DrawerContent>
            </Drawer>

            {/* Item Detail Modal */}
            <Modal isOpen={itemModal.isOpen} onClose={itemModal.onClose}>
                <ModalContent>
                    {(onClose) => (
                        <form
                            onSubmit={handleItemSubmit(onItemSubmit)}
                            noValidate
                        >
                            <ModalBody className="pt-5">
                                <div className="flex gap-4">
                                    <div className="h-24 w-24 shrink-0">
                                        {selectedItem?.image_url ? (
                                            <Image
                                                src={selectedItem.image_url}
                                                alt={selectedItem?.name}
                                                className="aspect-square w-full rounded-lg object-cover"
                                            />
                                        ) : (
                                            <div className="bg-default-100 flex h-full w-full items-center justify-center rounded-lg">
                                                <NoImageFallback
                                                    iconSize={32}
                                                    icon={LuPackage}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex flex-col gap-0.5">
                                        <ThemedText className="font-semibold">
                                            {selectedItem?.name}
                                        </ThemedText>
                                        <ThemedText
                                            size="sm"
                                            variant="secondary"
                                        >
                                            Kategori: {selectedItem?.category}
                                        </ThemedText>
                                        {itemFormValues.warehouse_id && (
                                            <ThemedText
                                                size="sm"
                                                variant={
                                                    getCurrentStock(
                                                        itemFormValues.warehouse_id,
                                                    ) > 0
                                                        ? "success"
                                                        : "danger"
                                                }
                                            >
                                                Stok saat ini:{" "}
                                                {getCurrentStock(
                                                    itemFormValues.warehouse_id,
                                                )}{" "}
                                                {itemFormValues.unit}
                                            </ThemedText>
                                        )}
                                    </div>
                                </div>

                                {/* Row 1: Satuan & Jumlah */}
                                <div className="border-default-300 mt-4 grid grid-cols-2 gap-4 border-t border-dashed pt-4">
                                    {/* Unit Selection */}
                                    <Controller
                                        name="unit_id"
                                        control={itemControl}
                                        rules={{
                                            required: "Satuan wajib dipilih",
                                        }}
                                        render={({ field }) => (
                                            <Autocomplete
                                                label="Satuan"
                                                placeholder="Pilih satuan"
                                                variant="bordered"
                                                selectedKey={
                                                    field.value
                                                        ? String(field.value)
                                                        : null
                                                }
                                                onSelectionChange={(val) => {
                                                    field.onChange(val);
                                                    handleUnitChange(val);
                                                }}
                                                isDisabled={
                                                    !selectedItem?.units?.length
                                                }
                                                isRequired
                                                isInvalid={!!itemErrors.unit_id}
                                                errorMessage={
                                                    itemErrors.unit_id?.message
                                                }
                                            >
                                                {(
                                                    selectedItem?.units || []
                                                ).map((u) => (
                                                    <AutocompleteItem
                                                        key={String(u.unit_id)}
                                                        value={u.unit_id}
                                                        textValue={u.unit}
                                                    >
                                                        <div className="flex items-center justify-between gap-2">
                                                            <span>
                                                                {u.unit}
                                                            </span>
                                                            {u.is_default ? (
                                                                <Chip
                                                                    size="sm"
                                                                    color="primary"
                                                                    variant="flat"
                                                                >
                                                                    Default
                                                                </Chip>
                                                            ) : null}
                                                        </div>
                                                    </AutocompleteItem>
                                                ))}
                                            </Autocomplete>
                                        )}
                                    />

                                    <Controller
                                        name="quantity"
                                        control={itemControl}
                                        rules={{
                                            required: "Jumlah wajib diisi",
                                            min: {
                                                value: 1,
                                                message: "Minimal 1",
                                            },
                                        }}
                                        render={({ field }) => (
                                            <NumberInput
                                                label="Jumlah"
                                                placeholder="0"
                                                variant="bordered"
                                                minValue={1}
                                                value={field.value}
                                                onValueChange={field.onChange}
                                                hideStepper
                                                isClearable
                                                isWheelDisabled
                                                isRequired
                                                isInvalid={
                                                    !!itemErrors.quantity
                                                }
                                                errorMessage={
                                                    itemErrors.quantity?.message
                                                }
                                                endContent={
                                                    <ThemedText
                                                        size="sm"
                                                        variant="caption"
                                                    >
                                                        {itemFormValues.unit}
                                                    </ThemedText>
                                                }
                                            />
                                        )}
                                    />
                                </div>

                                {/* Row 2: Pilih Gudang Tujuan */}
                                <Controller
                                    name="warehouse_id"
                                    control={itemControl}
                                    rules={{ required: "Gudang wajib dipilih" }}
                                    render={({ field }) => {
                                        return (
                                            <Autocomplete
                                                label="Gudang Tujuan"
                                                placeholder="Pilih gudang tujuan"
                                                variant="bordered"
                                                isLoading={
                                                    fetchWarehouses.isLoading
                                                }
                                                selectedKey={
                                                    field.value
                                                        ? String(field.value)
                                                        : null
                                                }
                                                onSelectionChange={(val) => {
                                                    field.onChange(val);
                                                    handleWarehouseChange(val);
                                                }}
                                                isDisabled={
                                                    !itemFormValues.unit_id
                                                }
                                                isRequired
                                                isInvalid={
                                                    !!itemErrors.warehouse_id
                                                }
                                                errorMessage={
                                                    itemErrors.warehouse_id
                                                        ?.message
                                                }
                                                description="Barang akan masuk ke gudang ini"
                                            >
                                                {(
                                                    fetchWarehouses.data || []
                                                ).map((w) => {
                                                    const currentStock =
                                                        getCurrentStock(
                                                            w.value,
                                                        );
                                                    return (
                                                        <AutocompleteItem
                                                            key={String(
                                                                w.value,
                                                            )}
                                                            value={w.value}
                                                            textValue={w.title}
                                                        >
                                                            <div className="flex items-center justify-between gap-2">
                                                                <span>
                                                                    {w.title}
                                                                </span>
                                                                <Chip
                                                                    size="sm"
                                                                    variant="flat"
                                                                    color={
                                                                        currentStock >
                                                                        0
                                                                            ? "success"
                                                                            : "danger"
                                                                    }
                                                                >
                                                                    {
                                                                        currentStock
                                                                    }{" "}
                                                                    {
                                                                        itemFormValues.unit
                                                                    }
                                                                </Chip>
                                                            </div>
                                                        </AutocompleteItem>
                                                    );
                                                })}
                                            </Autocomplete>
                                        );
                                    }}
                                />

                                {/* Row 3: Harga Satuan */}
                                <div className="grid grid-cols-1 gap-4">
                                    <Controller
                                        name="price"
                                        control={itemControl}
                                        rules={{
                                            required: "Harga wajib diisi",
                                        }}
                                        render={({ field }) => (
                                            <NumberInput
                                                label="Harga Beli per Satuan"
                                                placeholder="0"
                                                variant="bordered"
                                                minValue={0}
                                                value={field.value}
                                                onValueChange={field.onChange}
                                                hideStepper
                                                isClearable
                                                isWheelDisabled
                                                isRequired
                                                isInvalid={!!itemErrors.price}
                                                errorMessage={
                                                    itemErrors.price?.message
                                                }
                                                startContent={
                                                    <ThemedText
                                                        size="sm"
                                                        variant="secondary"
                                                    >
                                                        Rp
                                                    </ThemedText>
                                                }
                                                formatOptions={{
                                                    style: "decimal",
                                                    maximumFractionDigits: 0,
                                                }}
                                                description="Harga dapat disesuaikan jika diperlukan."
                                            />
                                        )}
                                    />
                                </div>

                                <div className="border-default-300 mt-4 flex items-center justify-between border-t border-dashed pt-4">
                                    <ThemedText className="font-medium">
                                        Subtotal
                                    </ThemedText>
                                    <ThemedText
                                        variant="primary"
                                        className="font-semibold"
                                    >
                                        {formatCurrency(
                                            itemFormValues.quantity *
                                                itemFormValues.price,
                                        )}
                                    </ThemedText>
                                </div>
                            </ModalBody>
                            <ModalFooter>
                                <Button
                                    variant="light"
                                    color="danger"
                                    size="sm"
                                    onPress={onClose}
                                >
                                    Batal
                                </Button>
                                <Button color="primary" size="sm" type="submit">
                                    Tambah ke Daftar
                                </Button>
                            </ModalFooter>
                        </form>
                    )}
                </ModalContent>
            </Modal>
        </div>
    );
};

export default PurchaseOrderCreate;
